import { useState } from "react";
import { motion } from "framer-motion";
import { Search, PackageCheck, CreditCard, Check } from "lucide-react";
import ShopNav from "../../components/shop/ShopNav";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";
const PROD_STEPS = ["planned", "in_production", "qc", "completed", "shipped"];

export default function ShopTrack() {
  const { t } = useI18n();
  const [f, setF] = useState({ order_id: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setResult(null); setLoading(true);
    try {
      const { data } = await api.post("/shop/track", { order_id: f.order_id.trim(), email: f.email.trim() });
      setResult(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || t("shop.track.notFound"));
    } finally {
      setLoading(false);
    }
  };

  const orderStatus = (s) => t(`shop.track.orderStatus.${s}`) || s;
  const prodStatus = (s) => t(`shop.track.prodStatus.${s || "none"}`);

  return (
    <div className="min-h-screen bg-cream text-navy">
      <ShopNav />
      <div className="max-w-xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-3">AURA · Commerce</div>
          <h1 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("shop.track.title")}</h1>
          <p className="mt-3 text-navy/60">{t("shop.track.subtitle")}</p>
        </motion.div>

        <form onSubmit={submit} data-testid="track-form" className="mt-8 bg-white border border-navy/10 rounded-xl p-6 space-y-3">
          <input required data-testid="track-order-id" placeholder={t("shop.track.orderId")} value={f.order_id}
            onChange={(e) => setF({ ...f, order_id: e.target.value })} className={inputCls} />
          <input required type="email" data-testid="track-email" placeholder={t("shop.track.email")} value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
          {err && <p className="text-sm text-destructive" data-testid="track-error">{err}</p>}
          <button data-testid="track-submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity disabled:opacity-60">
            <Search className="w-4 h-4" /> {loading ? t("shop.track.searching") : t("shop.track.submit")}
          </button>
        </form>

        {result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }}
            data-testid="track-result" className="mt-6 bg-white border border-navy/10 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline text-navy/40">{t("shop.track.resultTitle")}</div>
                <div className="font-mono text-lg mt-1">#{result.order_id}</div>
              </div>
              <PackageCheck className="w-6 h-6 text-emerald-500" />
            </div>
            <div className="mt-2 text-xs text-navy/40">{t("shop.track.placed")} {(result.created_at || "").slice(0, 10)}</div>

            {/* Order lifecycle status */}
            <div className="mt-5 rounded-lg border border-navy/10 p-4 bg-cream/40">
              <div className="flex items-center gap-2 text-navy/40 text-xs"><CreditCard className="w-4 h-4" /> {t("shop.track.orderLabel")}</div>
              <div className="mt-1 font-medium text-lg">{orderStatus(result.status)}</div>
              <p className="mt-1 text-sm text-navy/55 leading-relaxed">{t(`shop.track.statusMsg.${result.status}`)}</p>
            </div>

            {/* Scheduled message block */}
            {result.status === "scheduled" && result.review_message && (
              <div className="mt-4 rounded-lg border border-blue-500/20 bg-blue-500/5 p-4" data-testid="track-scheduled">
                <div className="overline text-navy/40 mb-2">{t("shop.track.availability")}</div>
                <p className="text-sm text-navy/70 leading-relaxed">{result.review_message}</p>
              </div>
            )}

            {/* Payment link block (shown whenever a link is available) */}
            {result.payment_link && (result.status === "payment_link_sent" || result.status === "scheduled") && (
              <div className="mt-4 rounded-lg border border-violet/20 bg-violet/5 p-4" data-testid="track-payment">
                {result.status === "payment_link_sent" && result.review_message && <p className="text-sm text-navy/70 mb-3">{result.review_message}</p>}
                {result.status === "scheduled" && <p className="text-sm text-navy/70 mb-3">{t("shop.track.payToStart")}</p>}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-navy/50">{t("shop.subtotal")}</span><span>{money(result.subtotal)} MXN</span></div>
                  <div className="flex justify-between"><span className="text-navy/50">{t("shop.track.shipping")}</span><span>{money(result.shipping_cost)} MXN</span></div>
                  <div className="flex justify-between font-medium pt-1 border-t border-navy/10 mt-1"><span>{t("shop.track.totalToPay")}</span><span className="text-violet">{money(result.total)} MXN</span></div>
                </div>
                <a href={result.payment_link} target="_blank" rel="noreferrer" data-testid="track-pay-btn"
                  className="mt-4 block text-center rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 transition-opacity">
                  {t("shop.track.payNow")}
                </a>
              </div>
            )}

            {/* Visual production timeline (only once paid & in production) */}
            {(result.status === "paid" || result.production_status) && (
            <div className="mt-6" data-testid="track-timeline">
              <div className="overline text-navy/40 mb-4">{t("shop.track.prodLabel")}</div>
              <div className="flex items-center">
                {PROD_STEPS.map((step, idx) => {
                  const currentIdx = PROD_STEPS.indexOf(result.production_status);
                  const reached = currentIdx >= idx;
                  const isCurrent = currentIdx === idx;
                  return (
                    <div key={step} className="flex items-center flex-1 last:flex-none">
                      <div className="flex flex-col items-center">
                        <div data-testid={`track-step-${step}`}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            reached ? "bg-gradient-violet text-white" : "bg-white border border-navy/15 text-navy/30"
                          } ${isCurrent ? "ring-4 ring-violet/15" : ""}`}>
                          {reached ? <Check className="w-4 h-4" /> : <span className="text-xs">{idx + 1}</span>}
                        </div>
                        <span className={`mt-2 text-[10px] text-center leading-tight max-w-[64px] ${reached ? "text-navy" : "text-navy/40"}`}>
                          {t(`shop.track.prodStatus.${step}`)}
                        </span>
                      </div>
                      {idx < PROD_STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1 -mt-5 transition-colors ${PROD_STEPS.indexOf(result.production_status) > idx ? "bg-violet" : "bg-navy/10"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            <div className="mt-5">
              <div className="overline text-navy/40 mb-2">{t("shop.track.itemsLabel")}</div>
              <ul className="space-y-1 text-sm">
                {(result.items || []).map((i, idx) => (
                  <li key={idx} className="flex justify-between border-b border-navy/5 py-1.5">
                    <span>{i.name}</span><span className="text-navy/50">×{i.qty}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between mt-3 pt-2 font-medium">
                <span className="text-navy/50">{t("shop.track.totalLabel")}</span>
                <span>{money(result.subtotal)} <span className="text-navy/30 text-xs">{result.currency}</span></span>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
